import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type MailResponseRateResult, type PrepareMailDropResult } from '@whinerp/client';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';

/**
 * A postal campaign (G-161): the mailing file for the mailing house, and
 * replies recorded by the code on the card.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

/** Hands the file to the browser as a download. */
function download(csv: string, name: string): void {
  try {
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.click();
    URL.revokeObjectURL(url);
  } catch {
    // A browser without object URLs still gets the counts on screen.
  }
}

export function DirectMailPanel({ campaignId, campaignName }: { campaignId: string; campaignName: string }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const rate = useQuery<MailResponseRateResult, ApiError>({
    queryKey: ['mail-responses', campaignId],
    queryFn: () => api.mailResponseRate({ path: { campaignId } }),
  });
  const drop = useMutation<PrepareMailDropResult, ApiError>({
    mutationFn: () => api.prepareMailDrop({ path: { campaignId } }),
    onSuccess: (result) => {
      download(result.csv, `${campaignName.replace(/[^\w-]+/g, '-')}-mailing.csv`);
      void queryClient.invalidateQueries({ queryKey: ['mail-responses', campaignId] });
    },
  });
  const respond = useMutation({
    mutationFn: () => api.recordMailResponse({ body: { code, note: note.trim() || null } }),
    onSuccess: () => {
      setCode('');
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['mail-responses', campaignId] });
    },
  });

  return (
    <Panel data-region="direct-mail">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('directMail.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('directMail.help')}</p>
        {rate.data ? (
          <p className="text-dense text-ink" data-mail-rate>
            {t('directMail.rate', { values: { mailed: String(rate.data.mailed), responded: String(rate.data.responded) } })}
          </p>
        ) : null}
        <div>
          <Button intent="primary" size="small" data-mail-drop disabled={drop.isPending} onClick={() => drop.mutate()}>
            {t('directMail.prepare')}
          </Button>
        </div>
        {drop.data ? (
          <div className="flex flex-col gap-1 text-dense" data-mail-drop-result>
            <span className="text-ink" data-mail-included>{t('directMail.included', { values: { count: String(drop.data.included) } })}</span>
            {drop.data.excluded.map((entry) => (
              <span key={entry.reason} className="text-ink-meta" data-mail-excluded>
                {t('directMail.excluded', { values: { count: String(entry.count), reason: entry.reason } })}
              </span>
            ))}
          </div>
        ) : null}
        {drop.error ? <p className="text-dense text-danger-text">{errorText(drop.error)}</p> : null}

        <form data-mail-response className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            respond.mutate();
          }}>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('directMail.code')}
            <input className={`${INPUT} w-36 uppercase`} required data-mail-code value={code}
              onChange={(event) => setCode(event.target.value)} />
          </label>
          <label className="flex flex-1 flex-col gap-1 text-meta text-ink-meta">
            {t('directMail.note')}
            <input className={INPUT} maxLength={2000} data-mail-note value={note}
              onChange={(event) => setNote(event.target.value)} />
          </label>
          <Button size="small" type="submit" data-mail-response-submit disabled={respond.isPending}>
            {t('directMail.record')}
          </Button>
        </form>
        {respond.data ? (
          <p className="text-dense text-ink" data-mail-recorded>
            {respond.data.firstResponse
              ? t('directMail.recorded', { values: { name: respond.data.contactName } })
              : t('directMail.already', { values: { name: respond.data.contactName } })}
          </p>
        ) : null}
        {respond.error ? <p className="text-dense text-danger-text" data-mail-response-error>{errorText(respond.error as ApiError)}</p> : null}
      </div>
    </Panel>
  );
}
